import { notification, Button } from "antd";
import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { useMutation } from "react-query";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../AppContext";
import GardenImage from "../assets/login-grungard4.png";
import GrunGuadLogo from "../assets/Logo-grungard.png";
import LoginForm from "../components/LoginForm";
import tr from "../i18n";
import { inquireVerifyCode } from "../redux/slices/Auth";
import { validEmail, validPhoneNumber } from "../utils/RegExUtility";
import "./Login.css";

const Login = () => {
  const [emailOrPhone, setEmailOrPhone] = useState(null);
  const [hasOTPSent, setHasOTPSent] = useState(false);
  const [tenantId, setTenantId] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setToken, fetchAllGardens } = useContext(AppContext);
  const [lang, setLang] = useState(localStorage.getItem("locale") || "en");

  const handleLangToggle = (value) => {
    setLang(value);
    localStorage.setItem("locale", value);
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      setToken(token);
      navigate("/control-center");
    }
  }, [navigate, setToken]);

  const validatePhoneNumberAndEmail = (e) => {
    return validEmail.test(e) || validPhoneNumber.test(e);
  };

  const generateValidInput = (input) => {
    return validPhoneNumber.test(input) ? `${input}` : input;
  };

  const showErrorNotification = ({ message, description }) => {
    const key = `open${Date.now()}`;
    notification.error({
      className: "grundard-notification",
      key,
      btn: (
        <Button danger size="small" onClick={() => notification.close(key)} type="primary">
          {tr("Close")}
        </Button>
      ),
      ...(process.env.NODE_ENV === "development" ? { duration: 9999 } : {}),
      message,
      ...(description ? { description } : {}),
      placement: "topRight",
    });
  };

  const showSuccessNotification = ({ message, description }) => {
    const key = `open${Date.now()}`;
    notification.success({
      className: "grundard-notification",
      key,
      btn: (
        <Button size="small" onClick={() => notification.close(key)} type="primary">
          {tr("Close")}
        </Button>
      ),
      ...(process.env.NODE_ENV === "development" ? { duration: 9999 } : {}),
      message,
      ...(description ? { description } : {}),
      placement: "topRight",
    });
  };

  const login = useMutation(
    ({ emailOrPhone, tenantId }) => {
      return axios.post(`${process.env.REACT_APP_GARDENER_API_END_POINT}/auth/login`, {
        emailOrPhone,
        tenantId,
      });
    },
    {
      onSuccess: (data, vars, ctx) => {
        // regex test data.data.error includes "User doesn't exist" case insensitively
        if (/User doesn't exist/i.test(data.data.error)) {
          showErrorNotification({
            message: tr("Failed to send code"),
            description: tr(`User not found!`),
          });
        } else if (data.data.error) {
          showErrorNotification({
            message: tr("Failed to send code"),
            description: `${data.data.error}`,
          });
        } else {
          showSuccessNotification({
            message: tr("Code sent"),
            description: tr(`The authentication code has been sent to`) + ` ${vars.emailOrPhone}`,
          });

          let userLocale = "en";
          setEmailOrPhone(vars.emailOrPhone);
          setHasOTPSent(true);
          setTenantId(vars.tenantId);
          if (vars.emailOrPhone.startsWith("90")) {
            userLocale = "tr";
          }
          localStorage.setItem("locale", userLocale);
        }
      },
      onError: (e) => {
        showErrorNotification({
          message: tr("An error occurred"),
        });
      },
    },
  );

  const onSend = async ({ phone, tenantId }) => {
    const trimmedInputValue = phone.replace(/\s+/g, "");
    if (!validatePhoneNumberAndEmail(trimmedInputValue)) {
      showErrorNotification({
        message: tr("The phone number you've entered is in an invalid format"),
        description: tr("Phone number should be in 1xxxxxxxxxx format"),
      });
    } else {
      const validInput = generateValidInput(trimmedInputValue);
      login.mutate({ emailOrPhone: validInput, tenantId });
    }
  };

  const verifyCodeCall = (e) => {
    dispatch(
      inquireVerifyCode({
        emailOrPhone: emailOrPhone,
        code: e,
        tenantId,
      }),
    ).then((response) => {
      if (response?.payload?.payload?.success) {
        applyVerifySuccessOperations(response?.payload?.payload?.token, response?.payload?.payload?.settings, response?.payload?.payload?.type, emailOrPhone);
      } else {
        showErrorNotification({
          message: tr("Authentication failed"),
          description: tr("Invalid code"),
        });
      }
    });
  };

  const applyVerifySuccessOperations = (token, settings, type, emailOrPhone) => {
    localStorage.setItem("token", token);

    if (settings) {
      localStorage.setItem("settings", JSON.stringify(settings));
    }

    if (type) {
      localStorage.setItem("type", type);
    }

    sessionStorage.setItem("forceRefetchGardens", "1");
    sessionStorage.setItem("forceRefetchDevices", "1");

    setToken(token);

    fetchAllGardens({ background: false, force: true });

    localStorage.setItem(
      "user",
      JSON.stringify({
        username: emailOrPhone,
      }),
    );
    navigate("/control-center");
  };

  const onSubmit = async (e) => {
    verifyCodeCall(e);
  };

  return (
    <section>
      <div className="login-container flex min-h-screen">
        <div className="login-image-section flex-1 hidden sm:block">
          <img src={GardenImage} alt="Login Illustration" className="w-full h-full object-cover" />
        </div>

        <div className="login-form-section flex-1 flex flex-col justify-center p-4">
          <div className="absolute top-1 right-1 p-5 flex flex-col items-end gap-4">
            <img src={GrunGuadLogo} className="w-full" alt="" />

            <Button.Group size="small">
              <Button type={lang === "en" ? "primary" : "default"} onClick={() => handleLangToggle("en")}>
                EN
              </Button>
              <Button type={lang === "tr" ? "primary" : "default"} onClick={() => handleLangToggle("tr")}>
                TR
              </Button>
            </Button.Group>
          </div>
          <LoginForm emailOrPhone={emailOrPhone} hasOTPSent={hasOTPSent} onSubmit={onSubmit} onSend={onSend} />
        </div>
      </div>
    </section>
  );
};
export default Login;
